import {
	ContentTextTypeEnum,
	ContentTypeEnum,
	ValueTypeEnum
}                       from '@models/common.model';
import { IGroupConfig } from '@shared/components/group/group.model';

// ENUMS #############################################################
export enum TableRowItemEventsEnum {
	ALL = 'all',
	CLICK = 'click',
	HOVER = 'hover'
}

export enum TableResponsiveBreakpointEnum {
	MD = 'md',
	LG = 'lg',
	SM = 'sm',
	XL = 'xl',
}

export enum TableCaptionPositionEnum {
	BOTTOM_CENTER = 'bottom center',
	BOTTOM_LEFT = 'bottom left',
	BOTTOM_RIGHT = 'bottom right',
	TOP_CENTER = 'top center',
	TOP_LEFT = 'top left',
	TOP_RIGHT = 'top right'
}


export enum TableCellTypeEnum {
	HEADER = 'header',
	STANDARD = 'standard',
}

export enum TableCellHeaderScopeEnum {
	COL = 'col',
	COL_GROUP = 'colgroup',
	ROW = 'row',
	ROW_GROUP = 'rowgroup',
}


// INTERFACES ########################################################

/**
 * The TableConfig Interface for a multiple grouped Tables
 *
 * @[ key: string ] {ITableConfig} the tableConfig for each group, key as group
 */
export interface IGroupedTableConfigData {
	[ key: string ]: ITableConfig;
}

/**
 * The TableConfig Interface for a single Table
 *
 * @identifier {string} table identifier, will id attribute as well
 * @groupConfig {IGroupConfig} configuration of the tables group component
 * @body {ITableBody} config and data for table body
 * @bordered {boolean} flag to show the table with borders
 * @rowsSpaced {boolean} flag to add some spaces (bottom borders) between table body rows
 * @caption {ITableCaption} config and data for table caption
 * @compact {boolean} flag to reduce padding value on each table cell, making it more compact
 * @footer {ITableFooter} config and data for table footer
 * @header {ITableHeader} config and data for table header
 * @hoverable {boolean} flag to show hover effects on rows
 * @responsive {{isActive:boolean, breakpoint: TableResponsiveBreakpointEnum}}} responsiveness config, shows scrollbars at specified breakpoint when active
 * @stripped {boolean} flag to for different colors on even/odd table rows
 */
export interface ITableConfig {
	identifier?: string;
	groupConfig?: IGroupConfig;
	body?: ITableBody;
	bordered?: boolean;
	rowsSpaced?: boolean;
	caption?: ITableCaption;
	compact?: boolean;
	footer?: ITableFooter;
	header?: ITableHeader;
	hoverable?: boolean;
	responsive?: {
		isActive: boolean;
		breakpoint: TableResponsiveBreakpointEnum;
	};
	stripped?: boolean;
}

/**
 * Interface for Table Caption
 *
 * @position {TableCaptionPositionEnum} the postion of the caption either below or above the table and left, centered or right aligned
 * @spareValue {any} fallback value
 * @value {any} for valueType i18n the value has to be an absolute i18n path | for value valueType any value type can be set
 * @valueType {ValueTypeEnum} either i18n or value, affects the value prop
 */
export interface ITableCaption extends ITableSectionBaseProperties {
	position?: TableCaptionPositionEnum;
	value: any;
	spareValue?: any;
	valueType: ValueTypeEnum;
}

/**
 * Interface for Table Header
 *
 * @rowData {boolean} data for header rows
 */
export interface ITableHeader extends ITableSectionBaseProperties {
	rowData?: ITableRowHeader[];
}

/**
 * Interface for Table Body
 *
 * @rowData {boolean} data for body rows
 */
export interface ITableBody extends ITableSectionBaseProperties {
	rowData?: ITableRowBase[];
}

/**
 * Interface for Table Footer
 *
 * @rowData {boolean} data for footer rows
 */
export interface ITableFooter extends ITableSectionBaseProperties {
	rowData?: ITableRowBase[];
}

/**
 * Optional Table Sections Base properties
 *
 * @class {string} custom class for table section
 * @contentType {ContentTypeEnum} contents can be either projected or default. On default rowData and values are used, default value is: DEFAULT
 * @isHidden {boolean} flag for hiding in DOM, default is: false
 * @isRendered {boolean} flag to render in DOM, default is: [caption, header, footer] = false, [body] = true, on projected contentType default is always true
 */
export interface ITableSectionBaseProperties {
	class?: string;
	contentType?: ContentTypeEnum;
	isHidden?: boolean;
	isRendered?: boolean;
}

/**
 * Table Row Base properties
 *
 * @cellData {ITableCellHeader} base data for each cell of a header row
 * @class {string} custom class for table header rows
 */
export interface ITableRowHeader extends ITableRowBase {
	cellData: ITableCellHeader[];
	class?: string;
}

/**
 * Table Row Base properties
 *
 * @identifier {string} row identifier, will output row id on hover
 * @cellData {ITableCellBase} base data for each cell of a row
 * @class {string} custom class for table rows
 */
export interface ITableRowBase {
	identifier?: string;
	cellData: ITableCellBase[];
	class?: string;
	disableEventEmit?: boolean;
	isDisabled?: boolean;
}

/**
 * Table Row Item Events
 *
 * @identifier {string} identifier of the event emitting rowItem
 * @isHovered {boolean} hover state of a row
 */
export interface ITableRowItemEvents {
	identifier: string;
	event: TableRowItemEventType;
}

export interface ITableRowItemEventHover {
	eventId: TableRowItemEventsEnum.HOVER;
	isHovered?: boolean;
}

export interface ITableRowItemEventClicked {
	eventId: TableRowItemEventsEnum.CLICK;
	isClicked?: boolean;
}


/**
 * Table Cell Base properties
 *
 * @cellType {TableCellTypeEnum} sets cell type as header (th) or standard (td)
 * @class {string} custom class for table cell
 * @contentType {ContentTextTypeEnum} contents can be set as innerText or innerHtml. Default value is: innerText
 * @applyDomSanitizerToHtmlContent {boolean} flag for DOM sanitation when contentType is set to HTML. Default value is: true
 * @spareValue {any} fallback value
 * @value {any} for valueType i18n the value has to be an absolute i18n path | for value valueType any value type can be set
 * @valueType {ValueTypeEnum} either i18n or value, affects the value prop
 */
export interface ITableCellBase {
	attributes?: {
		colspan?: number;
		rowspan?: number;
	};
	cellType?: TableCellTypeEnum;
	class?: string;
	contentType?: ContentTextTypeEnum;
	applyDomSanitizerToHtmlContent?: boolean;
	enforceCustomTemplate?: boolean;
	identifier?: string;
	isDisabled?: boolean;
	spareValue?: any;
	value: any;
	valueType: ValueTypeEnum;
}

/**
 * Table Cell Header specific properties
 *
 * @scope {TableCellTypeEnum} specifies whether a header cell is a header for a column, row, or group of columns or rows
 */
export interface ITableCellHeader extends ITableCellBase {
	// not yet implemented
	// scope?: TableCellHeaderScopeEnum;
}


// TYPES #############################################################
export type TableRowItemEventType =
	ITableRowItemEventClicked
	| ITableRowItemEventHover;
